<template>
<div class="risk-score-history-widget">
  <div class="risk-score-history-widget__right">
    <div class="risk-score-history-widget__title">
      <div class="risk-score-history-widget__title__label">{{this.$t('dashboard.risk.score.history.label')}}</div>
      <div class="risk-score-history-widget__title__date"> {{formattedDate}} </div>
    </div>
    <div class='risk-score-history-widget__trend'>
      <compact-risk-score-percentage
          :color='startScore.color'
          :text='startScore.text'
          :score='startScore.score.toString()'
          :label='startScore.label'
      />
      <v-icon class='risk-score-history-widget__trend__transition'>arrow_right_alt</v-icon>
      <compact-risk-score-percentage
          :color='endScore.color'
          :text='endScore.text'
          :score='endScore.score.toString()'
          :label='endScore.label'
      />
    </div>
    <v-divider />
    <div class="risk-score-history-widget__legend-title">{{ getLegendTitle }}</div>
    <div class='risk-score-history-widget__legend'>
      <risk-score-legend v-for="(letter, index) in letters"
                         :text='letter.text'
                         :color='letter.color'
                         :letter='letter.letter'
                         :key='`risk-score-legend-${index}`'
                         :style='[showTooltip? {"display": "none"}:"" ]'
      />
      <risk-score-indicator
                          :value='currentValue'
                          :title='`risk.score.details.table.title.${currentTitle}`'
                          :subtitle='isDataMissing? "risk.score.data.missing": `risk.score.details.table.subtitle.${currentSubtitle}`'
                          :data-missing='isDataMissing'
                          :domains='[]'
                          :style='[showTooltip? "": {"display": "none"} ]'
      />
    </div>
  </div>
  <div class="risk-score-history-widget__left" ref="chartContainer">
      <svg id='risk-score-history-svg' style="padding-bottom: 1px; overflow: visible;" :height="height" width="100%" :viewBox="`0 0 ${actualGraphWidth+widthOffset} ${height-heightOffset}`">
      </svg>
  </div>
</div>
</template>

<script>
import moment from 'moment';
import * as d3 from 'd3';
import _ from 'lodash';
import { advancedRound } from '@/utils/numberUtils';
import CompactRiskScorePercentage from '@/components/common/CompactRiskScorePercentage.vue';
import RiskScoreLegend from '@/components/common/RiskScoreLegend.vue';
import RiskScoreIndicator from '@/components/risk-score/RiskScoreIndicator.vue';
import current from 'element-ui/packages/table/src/store/current';

export default {
  name: 'RiskScoreHistoryWidget',
  components: { RiskScoreIndicator, RiskScoreLegend, CompactRiskScorePercentage },
  props: {
    graphWidth: {
      type: Number,
      default: () => 200,
    },
    height: {
      type: Number,
      default: () => 207,
    },
    data: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data() {
    return {
      widthOffset: 6,
      heightOffset: 28,
      actualGraphWidth: 200, // updated by ResizeObserver to match container
      resizeObserver: null,
      currentValue: 55,
      currentTitle: 'hijack',
      currentSubtitle: 'hijack',
      isDataMissing: false,
      showTooltip: false,
      letters: [
        {
          letter: 'a',
          color: '#29CE9C',
          text: '91-100',
        },
        {
          letter: 'b',
          color: '#F0BF16',
          text: '81-90',
        },
        {
          letter: 'c',
          color: '#FBA212',
          text: '71-80',
        },
        {
          letter: 'd',
          color: '#FF7A03',
          text: '61-70',
        },
        {
          letter: 'e',
          color: '#ED3D68',
          text: '51-60',
        },
        {
          letter: 'f',
          color: '#E132A0',
          text: '0-50',
        },
      ],
      seriesMax: {
        hijack: 5,
        smtp: 10,
        dns: 5,
        cloud: 10,
        dataleaks: 7,
        employees: 15,
        waf: 13,
        tech: 15,
        ports: 20,
      },
      categories: ['hijack', 'smtp', 'dns', 'cloud', 'dataleaks', 'employees', 'waf', 'tech', 'ports'],
    };
  },
  mounted() {
    this.resizeObserver = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect?.width;
      if (width && width > 0) {
        this.actualGraphWidth = Math.floor(width);
        if (!_.isEmpty(this.data)) this.buildChart();
      }
    });
    this.resizeObserver.observe(this.$refs.chartContainer);
    if (!_.isEmpty(this.data)) {
      this.buildChart();
    }
  },
  beforeDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  },
  watch: {
    data: {
      handler() {
        if (!_.isEmpty(this.data)) {
          this.buildChart();
        }
      },
      deep: true,
    },
    graphWidth: {
      handler() {
        if (!_.isEmpty(this.data)) {
          this.buildChart();
        }
      },
    },
  },
  computed: {
    current() {
      return current;
    },
    getLegendTitle() {
      return this.showTooltip ? this.$t('dashboard.risk.score.history.legend.indicator') : this.$t('dashboard.risk.score.history.legend.title');
    },
    formattedDate() {
      return this.data.length > 0 ? `${moment(new Date(this.data[0].date)).format('MMM yyyy')} - ${moment(new Date(this.data[this.data.length - 1].date)).format('MMM yyyy')}` : '';
    },
    startScore() {
      for (let i = 0; i < this.data.length; i += 1) {
        if (this.data[i].score !== 0) {
          const roundScore = advancedRound(this.data[i].score);
          const result = this.valueToResult(advancedRound(roundScore), false);
          const label = moment(new Date(this.data[i].date)).format('MMM yyyy');
          return {
            color: result.color,
            text: result.text,
            score: roundScore,
            label,
          };
        }
      }
      return {
        color: '#A9B6C3',
        text: 'N/A',
        score: '',
        label: '',
      };
    },
    endScore() {
      for (let i = this.data.length - 1; i >= 0; i -= 1) {
        if (this.data[i].score !== 0) {
          const roundScore = advancedRound(this.data[i].score);
          const result = this.valueToResult(advancedRound(roundScore), false);
          const label = moment(new Date(this.data[i].date)).format('MMM yyyy');
          return {
            color: result.color,
            text: result.text,
            score: roundScore,
            label,
          };
        }
      }
      return {
        color: '#A9B6C3',
        text: 'N/A',
        score: '',
        label: '',
      };
    },
  },
  methods: {
    buildChart() {
      const margin = {
        top: 10,
        right: 30,
        bottom: 30,
        left: 30,
      };
      const stack = d3.stack()
        .keys(this.categories)
        .value((d, key) => ((d.score !== 0) ? (d.details[key] + 10) : 0));
      const svg = d3.select('#risk-score-history-svg');
      svg.selectAll('*').remove();
      const xScale = d3.scaleBand(
        this.data.map(d => moment(new Date(d.date)).format('MMM yyyy')),
        [margin.left, this.actualGraphWidth - margin.right],
      ).padding(0.2);
      const yScale = d3.scaleLinear(
        [0, 190],
        [this.height - margin.bottom, margin.top],
      );
      const line = d3.line()
        .x(d => (xScale.bandwidth() / 2) + xScale(moment(new Date(d?.date)).format('MMM yyyy')))
        .y(d => yScale(d.score));
      const xAxis = d3.axisBottom(xScale)
        .tickSizeOuter(0);
      const chartData = stack(this.data);
      const groups = svg.append('g')
        .selectAll('g')
        .data(chartData)
        .join('g');

      groups.selectAll('rect')
        .data(d => {
          const temporary = _.cloneDeep(d);
          for (let i = 0; i < d.length; i += 1) {
            temporary[i].data = _.cloneDeep(d[i].data);
            temporary[i].data.key = d.key;
          }
          return temporary;
        })
        .join('rect')
        .attr('x', d => xScale(moment(new Date(d?.data.date)).format('MMM yyyy')))
        .attr('y', d => yScale(d[1]))
        .attr('height', d => yScale(d[0]) - yScale(d[1]))
        .attr('width', xScale.bandwidth())
        .attr('category-key', d => d.data.key)
        .style('fill', d => this.getBarColor(d))
        .style('stroke', d => `${this.getBarColor(d)}`)
        // eslint-disable-next-line no-unused-vars
        .on('mouseover', (e, d) => {
          const key = d?.data.key;
          let dataMissing = false;
          if (key === 'dataleaks') {
            dataMissing = d.data.riskObject.dataleaks;
          }
          if (key === 'employees') {
            dataMissing = d.data.riskObject.employees;
          }
          this.currentValue = advancedRound((100 * d?.data.details[key]) / this.seriesMax[key]);
          this.currentTitle = key;
          this.isDataMissing = dataMissing;
          this.currentSubtitle = key;
          this.showTooltip = true;
        })
        // eslint-disable-next-line no-unused-vars
        .on('mouseout', (e, d) => {
          this.showTooltip = false;
        });

      svg.append('g')
        .attr('transform', `translate(0,${ this.height - margin.bottom })`)
        .call(xAxis);

      const labels = svg.append('g')
        .selectAll('g')
        .data(this.data)
        .join('g');
      labels.selectAll('text')
        .data(this.data)
        .join('text')
        .text(d => {
          const result = this.valueToResult(advancedRound(d.score), false);
          return (d.score === 0 ? 'N/A' : `${result.text} ${advancedRound(d.score)}`);
        })
        .attr('text-anchor', 'start')
        // eslint-disable-next-line no-unused-vars
        .attr('x', d => (xScale.bandwidth() / 2) + xScale(moment(new Date(d?.date)).format('MMM yyyy')))
        // eslint-disable-next-line no-unused-vars
        .attr('y', (d, i) => (yScale(d.score) - (d.score === 0 ? 10 : 90)))
        .attr('class', 'risk-score-bar-label')
        .style('fill', d => this.valueToResult(advancedRound(d.score), d.score === 0).color);
      const widgetBg = getComputedStyle(document.documentElement).getPropertyValue('--bg-widget').trim() || '#F5F5F8';
      svg.append('path')
        .datum(this.getQuarterlyData(this.data))
        .attr('fill', 'none')
        .attr('stroke', widgetBg)
        .attr('stroke-width', 6)
        .attr('d', line);
      svg.append('path')
        .datum(this.getQuarterlyData(this.data))
        .attr('fill', 'none')
        .attr('stroke', '#ff7f00')
        .attr('stroke-width', 2.5)
        .attr('d', line);
      svg.selectAll('circle')
        .data(this.getQuarterlyData(this.data))
        .join('circle')
        .attr('cx', d => (xScale.bandwidth() / 2) + xScale(moment(new Date(d?.date)).format('MMM yyyy')))
        .attr('cy', d => yScale(d.score))
      // eslint-disable-next-line no-unused-vars
        .attr('r', 5)
        .style('fill', '#ff7f00')
        .style('stroke', widgetBg)
        .style('stroke-width', '1.5');
    },
    getBarColor(d) {
      if (d.data.score === 0) {
        return 'none';
      }
      const {key} = d.data;
      let dataMissing = false;
      if (key === 'dataleaks') {
        dataMissing = d.data.riskObject.dataleaks;
      }
      if (key === 'employees') {
        dataMissing = d.data.riskObject.employees;
      }
      const normalizedValue = dataMissing ? d?.data.details[key] : advancedRound((100 * d?.data.details[key]) / this.seriesMax[key]);
      return this.valueToResult(normalizedValue, dataMissing).color;
    },
    valueToResult(value, dataMissing) {
      let result = 'n/a';
      let color = '#A9B6C3';
      if (dataMissing) {
        return {
          text: result,
          color,
        };
      }
      let scoreParsed = parseFloat(value);
      const fixedSum = (100 - scoreParsed).toFixed(2);
      scoreParsed = parseFloat(fixedSum);
      const roundScore = Math.ceil(scoreParsed);
      if (roundScore >= 50 && roundScore <= 100) { // F
        result = this.letters[this.letters.length - 1].letter;
        color = this.letters[this.letters.length - 1].color;
      }
      if (roundScore >= 40 && roundScore <= 49) { // E
        result = this.letters[this.letters.length - 2].letter;
        color = this.letters[this.letters.length - 2].color;
      }
      if (roundScore >= 30 && roundScore <= 39) { // D
        result = this.letters[this.letters.length - 3].letter;
        color = this.letters[this.letters.length - 3].color;
      }
      if (roundScore >= 20 && roundScore <= 29) { // C
        result = this.letters[this.letters.length - 4].letter;
        color = this.letters[this.letters.length - 4].color;
      }
      if (roundScore >= 10 && roundScore <= 19) { // B
        result = this.letters[this.letters.length - 5].letter;
        color = this.letters[this.letters.length - 5].color;
      }
      if (roundScore >= 0 && roundScore <= 9) { // A //[CYN-1274]
        result = this.letters[0].letter;
        color = this.letters[0].color;
      }
      return {
        text: result,
        color,
      };
    },
    getQuarterlyData(data) {
      const quarters = ['Jan', 'Apr', 'Jul', 'Oct'];
      const result = [];

      quarters.forEach(month => {
        // Find the index of the first month of the quarter
        const startIndex = data.findIndex(item => moment(new Date(item.date)).format('MMM yyyy').startsWith(month));

        if (startIndex !== -1) {
          // Look for the first non-zero number in the quarter
          const nonZeroIndex = data.findIndex((item, index) => index >= startIndex && item.score !== 0);

          // If a non-zero number is found within the quarter, add it to the result
          if (nonZeroIndex !== -1 && nonZeroIndex < startIndex + 3) {
            result.push(data[nonZeroIndex]);
          }
        }
      });
      const firstIndex = data.findIndex(item => item.score !== 0);
      const lastIndex = data.findLastIndex(item => item.score !== 0);
      if (firstIndex >= 0 && !result.some(item => moment(new Date(item.date)).format('MMM yyyy') === moment(new Date(data[firstIndex])).format('MMM yyyy'))) {
        result.push(data[firstIndex]);
      }
      if (lastIndex >= 0 && !result.some(item => moment(new Date(item.date)).format('MMM yyyy') === moment(new Date(data[lastIndex])).format('MMM yyyy'))) {
        result.push(data[lastIndex]);
      }
      return result.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.risk-score-history-widget {
  display: flex;
  flex-direction: row;
  border-radius: $radius-md;
  background-color: transparent;
  flex-grow: 1;

  &__trend {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-right: rem(19);
    margin-left: rem(22);

    &__transition {
      font-size: rem(36);
      font-weight: bold;
      color: var(--text-secondary);
    }
  }

  &__legend-title {
    font-family: $font-family-regular;
    font-size: $font-size-sm;
    padding-bottom: rem(3);
    letter-spacing: 0;
    line-height: rem(15);
    color: var(--text-primary);
    margin-left: rem(25);
    margin-top: rem(15);
  }

  &__legend {
    margin-left: rem(25);
    margin-top: $space-1;
    margin-right: $space-2;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
  }

  &__right {
    display: flex;
    flex-direction: column;
    background-color: var(--bg-widget);
    min-width: rem(370);
    max-width: rem(370);
  }

  &__left {
    flex-grow: 1;
    border: 1px solid var(--border-primary);
    border-radius: $radius-md;
    background-color: transparent;
  }

  &__title {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: rem(20) rem(25) 0;

    &__label {
      color: var(--text-secondary);
      font-family: $font-family-regular;
      font-size: $font-size-sm;
      letter-spacing: 1.1px;
      line-height: rem(15);
    }

    &__date {
      color: var(--text-muted);
      font-family: $font-family-regular;
      font-size: $font-size-sm;
      letter-spacing: 0;
      line-height: rem(15);
      text-align: right;
    }
  }
}
</style>

<style lang="scss">
@import '~@/assets/styles/design-system';

.risk-score-history-widget {
  .risk-score-bar-label {
    font-family: $font-family-bold;
    text-transform: capitalize;
    stroke: var(--bg-widget);
    stroke-width: 0.3px;
    font-size: $font-size-base;
    text-anchor: middle;
  }

  .v-divider {
    margin-left: rem(25) !important;
    margin-right: rem(25) !important;
    margin-top: 0;
  }
}
</style>
